/**
 * Global Modal Store — Manages shell-level modal visibility.
 *
 * Replaces the 6 modal-dialog instances in AngularJS index.html.
 * In embedded mode, also syncs to AngularJS $rootScope for backward compat.
 */

import { create } from 'zustand';
import { getMountConfig } from '@/mountConfig';

export type ModalName =
  | 'workOrders'
  | 'propertyText'
  | 'propertyImage'
  | 'notes'
  | 'vendorText'
  | 'auditBilling';

export interface ModalState {
  openModal: ModalName | null;
  modalProps: Record<string, unknown>;
  open: (name: ModalName, props?: Record<string, unknown>) => void;
  close: () => void;
  toggle: (name: ModalName, props?: Record<string, unknown>) => void;
}

/**
 * Maps modal names to the corresponding AngularJS $rootScope flag names.
 * Used for dual-write in embedded mode.
 */
const LEGACY_FLAG_MAP: Record<ModalName, string> = {
  workOrders: 'showPropertyWorkOrdersPanel',
  propertyText: 'showPropertyTextModal',
  propertyImage: 'showPropertyImageModal',
  notes: 'note',
  vendorText: 'showVendorTextModal',
  auditBilling: 'showBillingModal',
};

/**
 * Sync modal visibility to AngularJS $rootScope in embedded mode.
 */
function syncToLegacy(name: ModalName, isOpen: boolean) {
  const { mode } = getMountConfig();
  if (mode !== 'embedded') return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any;
    const root =
      win.$rootScope ?? win.angular?.element?.(document.body)?.injector()?.get?.('$rootScope');
    if (!root) return;

    const flag = LEGACY_FLAG_MAP[name];
    if (name === 'notes') {
      // Notes modal uses an object { modal: boolean, type: string, id: string }
      if (!root.note) root.note = {};
      root.note.modal = isOpen;
    } else {
      root[flag] = isOpen;
    }

    if (root.$applyAsync) root.$applyAsync();
  } catch {
    // Graceful no-op if AngularJS not available
  }
}

export const useModalStore = create<ModalState>((set, get) => ({
  openModal: null,
  modalProps: {},

  open: (name, props = {}) => {
    set({ openModal: name, modalProps: props });
    syncToLegacy(name, true);
  },

  close: () => {
    const current = get().openModal;
    set({ openModal: null, modalProps: {} });
    if (current) syncToLegacy(current, false);
  },

  toggle: (name, props = {}) => {
    const current = get().openModal;
    if (current === name) {
      get().close();
    } else {
      // Close any currently open modal before opening a new one
      if (current) syncToLegacy(current, false);
      get().open(name, props);
    }
  },
}));
